import { useMemo, useState } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell } from 'recharts';
import { X } from 'lucide-react';
import { IBCS } from './charts/HatchPattern';
import IbcsTooltip, { formatEur } from './charts/IbcsTooltip';
import type { OverviewData } from '../../types/cashCockpit';

interface Props {
    data: OverviewData;
}

export default function OverviewTab({ data }: Props) {
    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

    const currentMonthKey = data.asOfDate.slice(0, 7);

    // === Column chart data (trend) ===
    // Newest months on the LEFT → reverse chronological
    const trend = useMemo(() => {
        if (selectedCurrency) {
            // Filter series to the selected currency's EUR-equivalent over time
            const byMonth = new Map<string, number>();
            data.trend24MByCurrency
                .filter(r => r.currencyCode === selectedCurrency)
                .forEach(r => byMonth.set(r.yearMonth, r.amountEUR));
            // Use the trend24M spine so we get all 24 months (zero for missing)
            return [...data.trend24M].reverse().map(t => ({
                yearMonth: t.yearMonth,
                label: t.yearMonth.slice(2),
                totalEUR: byMonth.get(t.yearMonth) ?? 0,
            }));
        }
        return [...data.trend24M].reverse().map(t => ({
            yearMonth: t.yearMonth,
            label: t.yearMonth.slice(2),
            totalEUR: t.totalEUR,
        }));
    }, [data, selectedCurrency]);

    // === Bar chart data (currency split) ===
    // If a month is selected, show that month's currency breakdown; else use as-of snapshot
    const currencies = useMemo(() => {
        if (selectedMonth) {
            return data.trend24MByCurrency
                .filter(r => r.yearMonth === selectedMonth)
                .map(r => ({ currencyCode: r.currencyCode, amountFCY: null as number | null, amountEUR: r.amountEUR }))
                .sort((a, b) => b.amountEUR - a.amountEUR);
        }
        return data.currencySplit.map(c => ({ currencyCode: c.currencyCode, amountFCY: c.amountFCY as number | null, amountEUR: c.amountEUR }));
    }, [data, selectedMonth]);

    const tooltipTrend = (p: { active?: boolean; payload?: Array<{ payload: (typeof trend)[number] }> }) => {
        if (!p.active || !p.payload?.[0]) return null;
        const d = p.payload[0].payload;
        const isCurrent = d.yearMonth === currentMonthKey;
        return <IbcsTooltip
            title={d.yearMonth + (isCurrent ? ' · Current' : '') + (selectedCurrency ? ` · ${selectedCurrency}` : '')}
            rows={[
                { label: 'Cash', value: formatEur(d.totalEUR) },
                ...(isCurrent ? [{ label: 'As of', value: data.asOfDate }] : []),
            ]}
            note="click to filter currencies →"
        />;
    };

    const barChartTitle = selectedMonth
        ? `Currency Split · ${selectedMonth}`
        : 'Currency Split (EUR equivalent)';
    const trendChartTitle = selectedCurrency
        ? `Cash Trend · ${selectedCurrency} only (24M)`
        : 'Group Cash Trend (24 months)';

    const onColumnClick = (ym: string) => {
        setSelectedMonth(selectedMonth === ym ? null : ym);
    };
    const onBarClick = (code: string) => {
        setSelectedCurrency(selectedCurrency === code ? null : code);
    };

    return (
        <div className="space-y-4">
            {/* Selection banner with clear button */}
            {(selectedMonth || selectedCurrency) && (
                <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                    <span className="text-blue-900 font-medium">Cross-filter active:</span>
                    {selectedMonth && (
                        <span className="inline-flex items-center gap-1 bg-white border border-blue-300 rounded px-2 py-0.5 text-xs">
                            Month: <span className="font-mono font-semibold">{selectedMonth}</span>
                            <button onClick={() => setSelectedMonth(null)} className="text-blue-700 hover:text-blue-900 ml-1">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    {selectedCurrency && (
                        <span className="inline-flex items-center gap-1 bg-white border border-blue-300 rounded px-2 py-0.5 text-xs">
                            Currency: <span className="font-mono font-semibold">{selectedCurrency}</span>
                            <button onClick={() => setSelectedCurrency(null)} className="text-blue-700 hover:text-blue-900 ml-1">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    )}
                    <button
                        onClick={() => { setSelectedMonth(null); setSelectedCurrency(null); }}
                        className="ml-auto text-xs text-blue-700 hover:text-blue-900 underline"
                    >Clear all</button>
                </div>
            )}

            {/* 24-month trend */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{trendChartTitle}</h3>
                <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={trend} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid stroke={IBCS.gridLine} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke={IBCS.actualLight} />
                        <YAxis tick={{ fontSize: 11 }} stroke={IBCS.actualLight} tickFormatter={v => formatEur(v)} width={72} />
                        <ReferenceLine y={0} stroke={IBCS.actual} />
                        <Tooltip content={tooltipTrend as never} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                        <Bar
                            dataKey="totalEUR"
                            name="Cash"
                            radius={[1, 1, 0, 0]}
                            onClick={(d: unknown) => {
                                const payload = (d as { payload?: { yearMonth?: string } }).payload;
                                if (payload?.yearMonth) onColumnClick(payload.yearMonth);
                            }}
                            cursor="pointer"
                        >
                            {trend.map((entry) => {
                                const isCurrent = entry.yearMonth === currentMonthKey;
                                return (
                                    <Cell
                                        key={entry.yearMonth}
                                        fill={
                                            selectedMonth === entry.yearMonth
                                                ? IBCS.positive
                                                : selectedMonth
                                                    ? '#D1D5DB'      // faded when another month is selected
                                                    : IBCS.actual
                                        }
                                        stroke={isCurrent ? '#F59E0B' : undefined}
                                        strokeWidth={isCurrent ? 2 : 0}
                                    />
                                );
                            })}
                        </Bar>
                    </ComposedChart>
                </ResponsiveContainer>
                <div className="text-xs text-gray-500 mt-1 text-center flex items-center justify-center gap-3">
                    <span>Click a month to filter the currency split below.</span>
                    <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-3 h-3 border-2 border-amber-500 bg-transparent" />
                        <span>Current month ({currentMonthKey})</span>
                    </span>
                </div>
            </div>

            {/* Currency split — horizontal bar */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{barChartTitle}</h3>
                <div className="space-y-1.5">
                    {currencies.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4">No currency data for this month.</div>
                    )}
                    {currencies.map((c) => {
                        const maxEUR = Math.max(...currencies.map(x => Math.abs(x.amountEUR)), 1);
                        const pct = (Math.abs(c.amountEUR) / maxEUR) * 100;
                        const active = selectedCurrency === c.currencyCode;
                        const dim = selectedCurrency && !active;
                        return (
                            <button
                                key={c.currencyCode}
                                onClick={() => onBarClick(c.currencyCode)}
                                className={`w-full flex items-center gap-2 text-left transition-opacity ${dim ? 'opacity-40' : ''}`}
                                title="Click to filter trend chart above"
                            >
                                <div className={`w-10 text-xs font-mono font-semibold ${active ? 'text-[#2C7BB6]' : 'text-gray-700'}`}>
                                    {c.currencyCode}
                                </div>
                                <div className="flex-1 relative h-6 bg-gray-100">
                                    <div
                                        className="absolute top-0 bottom-0 left-0 transition-all"
                                        style={{
                                            width: `${Math.max(pct, 2)}%`,
                                            backgroundColor: active ? IBCS.positive : IBCS.actual,
                                        }}
                                    />
                                </div>
                                <div className="w-32 text-right text-xs font-mono tabular-nums">
                                    {formatEur(c.amountEUR)}
                                </div>
                                <div className="w-32 text-right text-xs font-mono text-gray-500 tabular-nums">
                                    {c.amountFCY != null ? `(${c.amountFCY.toLocaleString('de-CH')} ${c.currencyCode})` : ''}
                                </div>
                            </button>
                        );
                    })}
                </div>
                <div className="text-xs text-gray-500 mt-2 text-center">Click a currency to filter the trend chart above.</div>
            </div>
        </div>
    );
}
